import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import evaluatorService from '@/lib/api/evaluator';
import { queryKeys } from '@/queries/keys';
import type {
  PersonaGenerateRequest,
  DatasetItemUpdateRequest,
} from '@/types/evaluator';

export function useEvalDatasets(orgId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.evaluator.datasets(orgId!),
    queryFn: async () => {
      const response = await evaluatorService.listDatasets(orgId!);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch datasets');
      }
      return response.data;
    },
    enabled: !!orgId,
  });
}

export function useEvalDatasetDetail(name: string | null, orgId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.evaluator.dataset(name!, orgId!),
    queryFn: async () => {
      const response = await evaluatorService.getDatasetDetail(name!, orgId!);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch dataset detail');
      }
      return response.data;
    },
    enabled: !!name && !!orgId,
  });
}

export function useDatasetItem(datasetName: string | null, itemId: string | null, orgId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.evaluator.datasetItem(datasetName!, itemId!, orgId!),
    queryFn: async () => {
      const response = await evaluatorService.getDatasetItem(datasetName!, itemId!, orgId!);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch dataset item');
      }
      return response.data;
    },
    enabled: !!datasetName && !!itemId && !!orgId,
  });
}

export function useGeneratePersonas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: PersonaGenerateRequest) => {
      const response = await evaluatorService.generatePersonas(data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to generate personas');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.evaluator.datasets(variables.organization_id) });
    },
  });
}

export function useUpdateDatasetItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      datasetName,
      itemId,
      organizationId,
      data,
    }: {
      datasetName: string;
      itemId: string;
      organizationId: string;
      data: DatasetItemUpdateRequest;
    }) => {
      const response = await evaluatorService.updateDatasetItem(datasetName, itemId, organizationId, data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update dataset item');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.evaluator.datasetItem(variables.datasetName, variables.itemId, variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.evaluator.dataset(variables.datasetName, variables.organizationId),
      });
    },
  });
}

export function useDeleteDatasetItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      datasetName,
      itemId,
      organizationId,
    }: {
      datasetName: string;
      itemId: string;
      organizationId: string;
    }) => {
      const response = await evaluatorService.deleteDatasetItem(datasetName, itemId, organizationId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to delete dataset item');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.evaluator.dataset(variables.datasetName, variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.evaluator.datasets(variables.organizationId),
      });
    },
  });
}

export function useDeleteDataset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      organizationId,
    }: {
      name: string;
      organizationId: string;
    }) => {
      const response = await evaluatorService.deleteDataset(name, organizationId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to delete dataset');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.evaluator.datasets(variables.organizationId),
      });
    },
  });
}
